/**
 * Shared realtime WebSocket hub — one socket per browser tab, ref-counted
 * subscriptions for dashboard / devices list / device detail.
 */
import { createRealtimeSession } from '@/core/api/realtimeClient';

function selectorKey(sel) {
  if (!sel || typeof sel !== 'object') return String(sel);
  return JSON.stringify(sel);
}

function createHub() {
  let session = null;
  let connectPromise = null;
  const listeners = new Set();
  /** @type {Map<string, { sel: object, count: number }>} */
  const tagRefs = new Map();

  function broadcast(msg, rawEv) {
    for (const fn of listeners) {
      try {
        fn(msg, rawEv);
      } catch (_) { /* ignore listener errors */ }
    }
  }

  function allSelectors() {
    return [...tagRefs.values()].map((e) => e.sel);
  }

  function pushSubscribe() {
    if (!session) return;
    const tags = allSelectors();
    if (tags.length > 0) session.subscribe(tags);
  }

  async function ensureConnected() {
    if (session?.getSocket()?.readyState === 1) return session;
    if (connectPromise) return connectPromise;
    connectPromise = (async () => {
      if (session) {
        try {
          session.disconnect();
        } catch (_) { /* ignore */ }
        session = null;
      }
      const next = createRealtimeSession({
        autoReconnect: true,
        onOpen: () => {
          broadcast({ type: '_hub_open' });
          pushSubscribe();
        },
        onClose: () => broadcast({ type: '_hub_close' }),
        onError: (err) => broadcast({ type: '_hub_error', payload: err }),
        onMessage: (msg, ev) => broadcast(msg, ev),
      });
      await next.connectWithTicket();
      session = next;
      pushSubscribe();
      return next;
    })();
    try {
      return await connectPromise;
    } finally {
      connectPromise = null;
    }
  }

  /**
   * @param {object[]} selectors
   * @param {(msg: object, ev?: Event) => void} listener
   * @returns {Promise<() => void>} release function
   */
  async function acquire(selectors, listener) {
    const list = Array.isArray(selectors) ? selectors.filter(Boolean) : [];
    listeners.add(listener);
    for (const sel of list) {
      const k = selectorKey(sel);
      const cur = tagRefs.get(k);
      if (cur) cur.count += 1;
      else tagRefs.set(k, { sel, count: 1 });
    }
    try {
      await ensureConnected();
      pushSubscribe();
    } catch (err) {
      listeners.delete(listener);
      for (const sel of list) {
        const k = selectorKey(sel);
        const cur = tagRefs.get(k);
        if (!cur) continue;
        cur.count -= 1;
        if (cur.count <= 0) tagRefs.delete(k);
      }
      throw err;
    }

    let released = false;
    return () => {
      if (released) return;
      released = true;
      listeners.delete(listener);
      for (const sel of list) {
        const k = selectorKey(sel);
        const cur = tagRefs.get(k);
        if (!cur) continue;
        cur.count -= 1;
        if (cur.count <= 0) tagRefs.delete(k);
      }
      if (listeners.size === 0) {
        try {
          session?.disconnect();
        } catch (_) { /* ignore */ }
        session = null;
        tagRefs.clear();
      } else {
        pushSubscribe();
      }
    };
  }

  return { acquire };
}

let hubSingleton = null;

function getHub() {
  if (!hubSingleton) hubSingleton = createHub();
  return hubSingleton;
}

/**
 * Share one WS across hooks. Call release() on unmount.
 * @param {object[]} selectors
 * @param {(msg: object) => void} onMessage
 * @returns {Promise<() => void>}
 */
export function acquireRealtimeSubscription(selectors, onMessage) {
  return getHub().acquire(selectors, onMessage);
}
